// ── Board filter persistence (TPT25) ──
//
// Board filters are URL-only state (board.js reads them fresh from getQueryParams() on
// every mount) — but the router drops the hash query string on every route change
// (router.js's resolve()/navigate() only ever see the path), and the sidebar's Board/
// Backlog links carry no query at all. So Board -> Backlog -> Board always came back
// with a bare hash, silently discarding whatever the user had selected. This module
// caches the last selection per project so board.js can fall back to it when the URL
// itself carries nothing — see tt-web-filters.md § Filter persistence.
//
// Stores the ALREADY-SERIALIZED query string (filtersToParams(filters).toString()), not
// a parsed JSON object: filtersToParams()/filtersFromParams() are already the canonical
// (de)serialization, including the `sprint=all` sentinel that lets board.js tell "user
// picked All Sprints" apart from "nothing saved yet" (see board-filters.js). Storing the
// string means loadFilterPrefs() hands back a URLSearchParams board.js can feed straight
// into the same filtersFromParams()/params.has('sprint') logic it already runs against
// the URL — no second schema to keep in sync.
//
// Same house style as lib/task-draft.js: tt-<feature>:<projectId> key, storageKey() null
// guard (no projectId -> no shared bucket), try/catch on every side so a full/blocked
// store degrades to "nothing saved" instead of breaking the board.

import { filtersToParams } from '../components/board-filters.js';

const KEY_PREFIX = 'tt-board-filters:';

function storageKey(projectId) {
  if (projectId === null || projectId === undefined || projectId === '') return null;
  return KEY_PREFIX + projectId;
}

// Persist the current filter selection for this project. Call only on a user-initiated
// change (board.js's onFilterChange) — never the mount-time Recent-5 preset, or that
// preset would freeze itself in as "the saved selection" the moment the page loads.
export function saveFilterPrefs(projectId, filters) {
  try {
    const key = storageKey(projectId);
    if (!key) return;
    localStorage.setItem(key, filtersToParams(filters).toString());
  } catch {
    /* best-effort cache only — a full/blocked store must not break the board */
  }
}

// Last-saved selection for this project, as a URLSearchParams (same shape
// getQueryParams() returns) — or null when nothing was ever saved, the stored value is
// blank, or storage isn't reachable. Callers feed the result straight into
// filtersFromParams()/params.has('sprint'), same as a real URL query string.
export function loadFilterPrefs(projectId) {
  try {
    const key = storageKey(projectId);
    if (!key) return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return new URLSearchParams(raw);
  } catch {
    return null;
  }
}

// Forget this project's saved selection — Clear All is an explicit "wipe it" action, not
// just another filter change (board.js routes it through here instead of
// saveFilterPrefs()).
export function clearFilterPrefs(projectId) {
  try {
    const key = storageKey(projectId);
    if (!key) return;
    localStorage.removeItem(key);
  } catch {
    /* nothing to clean up if storage isn't reachable in the first place */
  }
}
